import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  loginWithEmail,
  loginWithProvider,
  logoutUser,
  clearError,
  selectAuth,
  selectReturnUrl,
  setRememberMe,
  clearReturnUrl,
  selectSessionExpired,
  clearSessionExpired,
} from "@/store/slices/authSlice";
import type { FieldErrors } from "@/store/slices/authSlice";
import { selectHasSeenFirstLoginWelcome } from "@/store/slices/onboardingSlice";

/**
 * Auth State (from Redux)
 */
export interface AuthViewModelState {
  isAuthenticated: boolean;
  isLoading: boolean;
  googleLoading: boolean;
  appleLoading: boolean;
  signInLoading: boolean;
  error: string | null;
  fieldErrors: FieldErrors | null;
  rememberMe: boolean;
  user: {
    id: string;
    email: string;
    name: string;
    customUserId?: string;
  } | null;
  /**
   * Flag indicating the session has expired
   * Story 4.9: Used to show session expired message on login page
   */
  sessionExpired: boolean;
}

/**
 * Auth Actions (user intents)
 */
export interface AuthViewModelActions {
  login: (
    email: string,
    password: string,
    rememberMe: boolean
  ) => Promise<void>;
  socialLogin: (provider: "google" | "apple") => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  setRememberMe: (value: boolean) => void;
}

/**
 * Auth Computed Properties
 */
export interface AuthViewModelComputed {
  hasError: boolean;
  errorMessage: string;
  hasFieldErrors: boolean;
  isAnyLoading: boolean;
  /**
   * Translated message for session expired
   * Story 4.9: Displayed on login page when session expires
   */
  sessionExpiredMessage: string;
}

/**
 * Auth ViewModel Return Type
 */
export interface AuthViewModel {
  state: AuthViewModelState;
  actions: AuthViewModelActions;
  computed: AuthViewModelComputed;
  onMount: () => (() => void) | void;
}

/**
 * useAuthViewModel Hook
 *
 * MVVM ViewModel for authentication.
 * Structure: { state, actions, computed, onMount }
 *
 * @returns ViewModel with all authentication logic
 */
export function useAuthViewModel(): AuthViewModel {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("auth"); // Use 'auth' namespace
  const auth = useAppSelector(selectAuth);
  const returnUrl = useAppSelector(selectReturnUrl);
  const sessionExpired = useAppSelector(selectSessionExpired);
  const hasSeenWelcome = useAppSelector(selectHasSeenFirstLoginWelcome);

  // State (mapped from Redux)
  const state: AuthViewModelState = {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    googleLoading: auth.googleLoading,
    appleLoading: auth.appleLoading,
    signInLoading: auth.signInLoading,
    error: auth.error,
    fieldErrors: auth.fieldErrors,
    rememberMe: auth.rememberMe,
    user: auth.user,
    sessionExpired,
  };

  // Actions (all user intents)
  // Note: React Compiler automatically memoizes these functions
  const actions: AuthViewModelActions = {
    login: async (email: string, password: string, rememberMe: boolean) => {
      try {
        await dispatch(
          loginWithEmail({ email, password, rememberMe })
        ).unwrap();

        // Check if there's a return URL (e.g., from NFC pairing page)
        if (returnUrl) {
          // Clear the return URL from Redux
          dispatch(clearReturnUrl());
          // Navigate to the original destination
          navigate(returnUrl);
        } else if (!hasSeenWelcome) {
          // First login - show welcome page (Story 3.9.a)
          navigate("/welcome");
        } else {
          // Returning user - go to profile
          navigate("/owner/profile");
        }
      } catch {
        // Error is already in Redux state
      }
    },

    socialLogin: async (provider: "google" | "apple") => {
      try {
        await dispatch(loginWithProvider({ provider })).unwrap();
        // Note: This will redirect to OAuth provider, so navigation is handled by Amplify
      } catch {
        // Error is already in Redux state
      }
    },

    logout: async () => {
      try {
        await dispatch(logoutUser()).unwrap();
        navigate("/auth/login", { replace: true });
      } catch {
        // Error is already in Redux state
      }
    },

    clearError: () => {
      dispatch(clearError());
    },

    setRememberMe: (value: boolean) => {
      dispatch(setRememberMe(value));
    },
  };

  // Computed properties
  const computed: AuthViewModelComputed = {
    hasError: state.error !== null,
    errorMessage: state.error ? t(state.error) : "",
    hasFieldErrors: state.fieldErrors !== null,
    isAnyLoading:
      state.googleLoading || state.appleLoading || state.signInLoading,
    sessionExpiredMessage: sessionExpired ? t("login.sessionExpired") : "",
  };

  // Lifecycle
  // Note: React Compiler automatically memoizes this function
  const onMount = () => {
    // Clear session expired message after it's been seen
    // Story 4.9: Use setTimeout to ensure message is displayed before clearing
    if (sessionExpired) {
      setTimeout(() => {
        dispatch(clearSessionExpired());
      }, 100);
    }

    // Cleanup function
    return () => {
      dispatch(clearError());
    };
  };

  return { state, actions, computed, onMount };
}
